import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, User, Mail, Phone, MapPin, Award, Calendar, Car, Clock, Save, Thermometer, Music, StickyNote } from 'lucide-react';
import { getClientProfile, updateClientProfile } from '../api';
import { toast } from '../hooks/use-toast';

const ClientProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '', phone: '', address: '',
    vehicle_preference: '', temperature_preference: '', music_preference: '', special_notes: ''
  });

  const tierConfig = {
    lobby: { color: 'bg-gray-700', label: 'Lobby' },
    condo: { color: 'bg-red-600', label: 'Condo' },
    penthouse: { color: 'bg-gradient-to-r from-yellow-500 to-yellow-700', label: 'Penthouse' }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/auth/me`, { credentials: 'include' });
        if (!response.ok) navigate('/portal/login');
      } catch { navigate('/portal/login'); }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await getClientProfile();
        setProfile(data);
        setFormData({
          name: data.name || '', phone: data.phone || '', address: data.address || '',
          vehicle_preference: data.vehicle_preference || '',
          temperature_preference: data.temperature_preference || '',
          music_preference: data.music_preference || '',
          special_notes: data.special_notes || ''
        });
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateClientProfile(formData);
      setProfile(prev => ({ ...prev, ...formData }));
      setEditMode(false);
      toast({ title: 'Profile Updated', description: 'Your preferences have been saved.' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update profile.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const tier = tierConfig[profile?.membership_tier] || tierConfig.lobby;

  if (loading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="client-profile-page">
      <header className="bg-black text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/portal')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <h1 className="text-lg sm:text-xl font-bold">My Profile</h1>
          </div>
          {!editMode ? (
            <Button variant="outline" size="sm" onClick={() => setEditMode(true)} className="border-gray-700 text-gray-300 text-xs sm:text-sm" data-testid="edit-profile-btn">
              Edit
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={saving} size="sm" className="bg-red-600 hover:bg-red-700" data-testid="save-profile-btn">
              <Save className="h-4 w-4 sm:mr-1" />{saving ? '...' : <span className="hidden sm:inline">Save</span>}
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Profile Header */}
        <Card className="p-5 sm:p-6 mb-6 border-gray-200" data-testid="profile-header-card">
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
              {profile?.picture ? (
                <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-red-100"><User className="h-8 w-8 sm:h-10 sm:w-10 text-red-600" /></div>
              )}
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-black truncate">{profile?.name}</h2>
              <p className="text-gray-600 text-sm truncate">{profile?.email}</p>
              <div className={`inline-block mt-2 px-3 py-1 rounded-full text-white text-xs font-semibold ${tier.color}`}>
                {tier.label} Member
              </div>
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
          <Card className="p-3 sm:p-4 border-gray-200 text-center">
            <Car className="h-6 w-6 sm:h-8 sm:w-8 text-red-600 mx-auto mb-1" />
            <p className="text-xl sm:text-3xl font-bold text-black">{profile?.total_rides || 0}</p>
            <p className="text-xs text-gray-500">Rides</p>
          </Card>
          <Card className="p-3 sm:p-4 border-gray-200 text-center">
            <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-red-600 mx-auto mb-1" />
            <p className="text-xl sm:text-3xl font-bold text-black">{profile?.total_hours || 0}</p>
            <p className="text-xs text-gray-500">Hours</p>
          </Card>
          <Card className="p-3 sm:p-4 border-gray-200 text-center">
            <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-red-600 mx-auto mb-1" />
            <p className="text-sm sm:text-base font-bold text-black">
              {profile?.member_since ? new Date(profile.member_since).toLocaleDateString('en-US', {month:'short', year:'numeric'}) : 'N/A'}
            </p>
            <p className="text-xs text-gray-500">Since</p>
          </Card>
        </div>

        {/* Contact Information */}
        <Card className="p-5 sm:p-6 border-gray-200 mb-6" data-testid="profile-contact-card">
          <h3 className="text-base font-bold text-black mb-5">Contact Information</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-600 flex items-center gap-2 mb-2 text-sm"><User className="h-4 w-4" /> Full Name</Label>
              {editMode ? <Input name="name" value={formData.name} onChange={handleChange} className="border-gray-200" data-testid="profile-name-input" />
                : <p className="text-black font-medium">{profile?.name || '-'}</p>}
            </div>
            <div>
              <Label className="text-gray-600 flex items-center gap-2 mb-2 text-sm"><Mail className="h-4 w-4" /> Email</Label>
              <p className="text-black font-medium text-sm">{profile?.email}</p>
              <p className="text-xs text-gray-400">Email cannot be changed</p>
            </div>
            <div>
              <Label className="text-gray-600 flex items-center gap-2 mb-2 text-sm"><Phone className="h-4 w-4" /> Phone</Label>
              {editMode ? <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="(404) 000-0000" className="border-gray-200" data-testid="profile-phone-input" />
                : <p className="text-black font-medium">{profile?.phone || '-'}</p>}
            </div>
            <div>
              <Label className="text-gray-600 flex items-center gap-2 mb-2 text-sm"><MapPin className="h-4 w-4" /> Address</Label>
              {editMode ? <Input name="address" value={formData.address} onChange={handleChange} placeholder="Your address" className="border-gray-200" data-testid="profile-address-input" />
                : <p className="text-black font-medium">{profile?.address || '-'}</p>}
            </div>
          </div>
        </Card>

        {/* Concierge Preferences */}
        <Card className="p-5 sm:p-6 border-gray-200 mb-6" data-testid="profile-preferences-card">
          <h3 className="text-base font-bold text-black mb-2">Concierge Preferences</h3>
          <p className="text-xs text-gray-500 mb-5">Your preferences are remembered for every ACE experience. Update anytime.</p>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-600 flex items-center gap-2 mb-2 text-sm"><Car className="h-4 w-4" /> Vehicle Preference</Label>
              {editMode ? (
                <Select value={formData.vehicle_preference} onValueChange={(v) => setFormData(p => ({...p, vehicle_preference: v}))}>
                  <SelectTrigger className="border-gray-200" data-testid="profile-vehicle-pref">
                    <SelectValue placeholder="Select preferred vehicle..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="escalade">Cadillac Escalade</SelectItem>
                    <SelectItem value="yukon-xl">GMC Yukon XL</SelectItem>
                    <SelectItem value="suburban">Suburban</SelectItem>
                    <SelectItem value="sedan">Mercedes S450 / BMW 760Li</SelectItem>
                    <SelectItem value="sprinter">Mercedes Sprinter</SelectItem>
                    <SelectItem value="maybach">Maybach</SelectItem>
                    <SelectItem value="rolls-royce">Rolls-Royce</SelectItem>
                    <SelectItem value="bentley">Bentley</SelectItem>
                    <SelectItem value="no-preference">No Preference</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-black font-medium capitalize">{formData.vehicle_preference?.replace('-', ' ') || '-'}</p>
              )}
            </div>
            <div>
              <Label className="text-gray-600 flex items-center gap-2 mb-2 text-sm"><Thermometer className="h-4 w-4" /> Temperature Preference</Label>
              {editMode ? (
                <Select value={formData.temperature_preference} onValueChange={(v) => setFormData(p => ({...p, temperature_preference: v}))}>
                  <SelectTrigger className="border-gray-200" data-testid="profile-temp-pref">
                    <SelectValue placeholder="Select temperature..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cold">Cold (65-68°F)</SelectItem>
                    <SelectItem value="cool">Cool (68-70°F)</SelectItem>
                    <SelectItem value="moderate">Moderate (70-72°F)</SelectItem>
                    <SelectItem value="warm">Warm (72-75°F)</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-black font-medium capitalize">{formData.temperature_preference || '-'}</p>
              )}
            </div>
            <div>
              <Label className="text-gray-600 flex items-center gap-2 mb-2 text-sm"><Music className="h-4 w-4" /> Music Preference</Label>
              {editMode ? (
                <Select value={formData.music_preference} onValueChange={(v) => setFormData(p => ({...p, music_preference: v}))}>
                  <SelectTrigger className="border-gray-200" data-testid="profile-music-pref">
                    <SelectValue placeholder="Select music style..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quiet">Quiet / No Music</SelectItem>
                    <SelectItem value="jazz">Jazz & Lounge</SelectItem>
                    <SelectItem value="classical">Classical</SelectItem>
                    <SelectItem value="r-and-b">R&B / Soul</SelectItem>
                    <SelectItem value="hip-hop">Hip-Hop</SelectItem>
                    <SelectItem value="pop">Pop / Contemporary</SelectItem>
                    <SelectItem value="ask-me">Ask Me Each Time</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-black font-medium capitalize">{formData.music_preference?.replace(/-/g, ' ') || '-'}</p>
              )}
            </div>
            <div>
              <Label className="text-gray-600 flex items-center gap-2 mb-2 text-sm"><StickyNote className="h-4 w-4" /> Special Notes</Label>
              {editMode ? (
                <Textarea name="special_notes" value={formData.special_notes} onChange={handleChange} rows={3}
                  placeholder="Any standing preferences, allergies, or notes for your concierge..."
                  className="border-gray-200" data-testid="profile-notes-input" />
              ) : (
                <p className="text-black font-medium text-sm">{formData.special_notes || '-'}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Upgrade CTA */}
        {profile?.membership_tier !== 'penthouse' && (
          <Card className="p-5 sm:p-6 border-red-200 bg-red-50" data-testid="profile-upgrade-cta">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <Award className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-black text-sm">Upgrade Your Membership</h4>
                  <p className="text-xs text-gray-600">Unlock more benefits and priority access with a higher tier.</p>
                </div>
              </div>
              <Button onClick={() => navigate('/membership')} size="sm" className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto">View Tiers</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClientProfile;
